import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAuth, useQueryParams } from "../contexts";
import { getQueryType, getRequestOptions } from "../helper-functions";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import {
    DefaultPagedResponse,
    Entity,
    Order,
    IUseQueryPaged,
    PagedResponse,
    QueryHookParams,
    QueryType,
    MutationResult,
    UnlockedPackedOrderRequest,
} from "../models";

interface IUseOrderQuery extends Omit<IUseQueryPaged<Order>, "updateMutation"> {
    upsertMutation: MutationResult<Order, unknown, Order>;
    unlockMutation: MutationResult<Order, unknown, void>;
    unlockPackedOrderMutation: MutationResult<Order, unknown, UnlockedPackedOrderRequest>;
}

interface IUseOrderQueryParams extends QueryHookParams {
    searchParams?: Record<string, string>;
}

export const useOrderQuery = (params?: IUseOrderQueryParams): IUseOrderQuery => {
    const orderId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const { isAuthorized } = useAuth();
    const { get, post, put, del } = useAxios();
    const { queryParams, sortParams, pageParams } = useQueryParams().getParamsApi(Entity.Orders);
    const url = Endpoints.Orders;
    const queryType = getQueryType(orderId, params?.queryType);

    const pagedListQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Order>>(
                getRequestOptions(
                    url,
                    { ...config, params: { ...config?.params, ...params?.searchParams } },
                    queryParams,
                    sortParams,
                    pageParams
                )
            ),
        queryKey: queryKeys.orders.pagedList({ ...queryParams, ...pageParams, ...sortParams, ...params?.searchParams }),
        placeholderData: DefaultPagedResponse,
        enabled: isAuthorized && queryType === QueryType.PagedList,
        cacheTime: 5000,
        staleTime: 5000,
    });

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Order>>(
                getRequestOptions(url, config, queryParams, sortParams, undefined, false, true)
            ).then((response) => response.items),
        queryKey: queryKeys.orders.list({ ...queryParams, ...sortParams }),
        placeholderData: [],
        enabled: false,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<Order>({ url: `${url}/${orderId}`, config }),
        queryKey: queryKeys.orders.detail(orderId),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === QueryType.Detail && !!params?.id,
        cacheTime: 5000,
        staleTime: 5000,
    });

    const createMutation = useGenericMutation({
        mutationFn: (data: Partial<Order>) => post<Partial<Order>, Order>({ url, data }),
        queryKey: queryKeys.orders.all(),
        keysToInvalidate: [queryKeys.orders.lists(), queryKeys.orders.pagedLists(), queryKeys.lists.all()],
        successToast: "Order created",
    });

    const upsertMutation = useGenericMutation({
        mutationFn: (data: Partial<Order>) => put<Partial<Order>, Order>({ url, data }),
        queryKey: queryKeys.orders.detail(orderId),
        keysToInvalidate: [
            queryKeys.orders.lists(),
            queryKeys.orders.pagedLists(),
            queryKeys.lists.all(),
            queryKeys.lists.all(),
        ],
        disableSuccessToast: true,
    });

    const unlockMutation = useGenericMutation({
        mutationFn: () => put<void, Order>({ url: `${url}/${orderId}/unlock` }),
        queryKey: queryKeys.orders.detail(orderId),
        keysToInvalidate: [],
        disableSuccessToast: true,
    });

    const unlockPackedOrderMutation = useGenericMutation({
        mutationFn: (data: UnlockedPackedOrderRequest) =>
            put<UnlockedPackedOrderRequest, Order>({ url: `${url}/unlock-packed`, data }),
        queryKey: queryKeys.orders.detail(orderId),
        keysToInvalidate: [],
        successToast: "Order unlocked",
    });

    const deleteMutation = useGenericMutation({
        mutationFn: () => del({ url: `${url}/${orderId}` }),
        queryKey: queryKeys.orders.detail(orderId),
        keysToInvalidate: [queryKeys.orders.lists(), queryKeys.orders.pagedLists()],
        successToast: "Order deleted",
    });

    return useMemo<IUseOrderQuery>(
        () => ({
            pagedListQuery,
            listQuery,
            detailQuery,
            createMutation,
            upsertMutation,
            deleteMutation,
            unlockPackedOrderMutation,
            unlockMutation,
        }),
        [
            pagedListQuery,
            listQuery,
            detailQuery,
            createMutation,
            upsertMutation,
            deleteMutation,
            unlockPackedOrderMutation,
            unlockMutation,
        ]
    );
};
